import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getIsReseller } from 'store/publisher/selectors';
import { Store } from 'components/common/types/Store.types';
import ResellerCreativeStatus from 'components/common/ResellerCreativeStatus/ResellerCreativeStatus';
import { CREATIVE_STATUS_BADGE, REJECTION_STATUS_BADGE, REJECTED_TYPE } from 'consts/creative';
import StatusRenderer from 'components/common/StatusRenderer';
import { CreativeStatus } from 'components/common/types/Creative.types';
import Card from 'lib/Card';
import { CardTheme } from 'lib/Card/Card.types';
import arrowLeftSvg from 'assets/icons/arrow_left.svg';
import arrowRightSvg from 'assets/icons/arrow_right.svg';
import Button from 'lib/Button';
import usePendoTracking from 'customHooks/usePendoTracking';
import { isValidIndex, getAspectRatio } from 'utils/numberUtils';
import PreviewInfoText from './PreviewInfoText';

const PreviewInfo: React.FC = () => {
  const pathParams = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const creativesIdsList = useSelector((state: Store) => state.pages.contentManagement.creatives.creativesIdsList);

  const { getPendoDataId } = usePendoTracking();

  const creativeId = pathParams.id;

  const creativeListIndex = creativesIdsList.findIndex((id) => id === creativeId);
  const hasNextPrev = creativeListIndex > -1;

  const isReseller = useSelector(getIsReseller);
  const { marketId, statusCode, moderation, externalId, dsp, width, height, uploadedBy, uploadedDate, uploadedTime } =
    useSelector((state: Store) => state.creative);

  const size = width && height ? `${width}X${height}` : '- X -';
  const aspectRatio = getAspectRatio(Number(width), Number(height));
  const statusRenderer =
    statusCode === CreativeStatus.REJECTED ? (
      <StatusRenderer
        value={moderation?.[0]?.automatic ? REJECTED_TYPE.AUTO : REJECTED_TYPE.MANUAL}
        statuses={REJECTION_STATUS_BADGE}
      />
    ) : (
      <StatusRenderer value={statusCode} statuses={CREATIVE_STATUS_BADGE} />
    );

  const navigateToCreative = (dir: 'next' | 'prev'): void => {
    const toCreativeId =
      dir === 'next' ? creativesIdsList[creativeListIndex + 1] : creativesIdsList[creativeListIndex - 1];

    navigate(
      `/content-management/previewCreative/${toCreativeId}`,
      isValidIndex(location.state?.page)
        ? {
            state: { page: location.state.page },
          }
        : undefined,
    );
  };

  return (
    <Card id="preview-info" theme={CardTheme.TRANSPARENT_WITH_BORDER}>
      <div className={`p-4 grid gap-4 items-start ${hasNextPrev ? 'grid-cols-[1fr_auto]' : 'grid-cols-1'}`}>
        <div className="min-w-0 flex flex-wrap items-center gap-y-4">
          <div className="pr-4">
            <p className="body-sm text-neutral-600">Status</p>
            {isReseller && creativeId ? (
              <span className="block relative mt-2 mb-2">
                <ResellerCreativeStatus marketId={marketId} statusCode={statusCode} />
              </span>
            ) : (
              statusRenderer
            )}
          </div>
          <PreviewInfoText label="Creative ID" description={externalId} />
          <PreviewInfoText label="DSP" description={dsp?.name} />
          <PreviewInfoText label="Size" description={size} />
          <PreviewInfoText label="Aspect Ratio" description={aspectRatio} />
          <div className="flex items-center">
            <PreviewInfoText label="User" description={uploadedBy} />
            <PreviewInfoText label="Date" description={uploadedDate} />
            <PreviewInfoText label="Time" description={uploadedTime} />
          </div>
        </div>
        {hasNextPrev ? (
          <div className="flex items-center gap-2 shrink-0 pt-1">
            <Button
              svg={arrowLeftSvg}
              isDisabled={creativeListIndex === 0}
              label="Previous"
              dataPendoId={getPendoDataId('Previous Creative Preview')}
              onClick={() => navigateToCreative('prev')}
            />
            <Button
              svg={arrowRightSvg}
              isDisabled={creativeListIndex === creativesIdsList.length - 1}
              label="Next"
              dataPendoId={getPendoDataId('Next Creative Preview')}
              onClick={() => navigateToCreative('next')}
            />
          </div>
        ) : null}
      </div>
    </Card>
  );
};

export default PreviewInfo;
